// Documentation structure and metadata
export const docsStructure = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'BookOpen',
    pages: [
      { 
        slug: 'introduction', 
        title: 'Introduction',
        description: 'Learn what Cogsmith is and what it can do'
      },
      { 
        slug: 'installation', 
        title: 'Installation',
        description: 'Step-by-step installation for all platforms'
      },
      { 
        slug: 'quick-start', 
        title: 'Quick Start',
        description: 'Get up and running in 5 minutes'
      },
      {
        slug: 'system-requirements',
        title: 'System Requirements',
        description: 'Hardware and software requirements'
      },
      {
        slug: 'licensing',
        title: 'Licensing & Features',
        description: 'Free vs Pro features and license management'
      },
    ],
  },
  {
    id: 'core-features',
    title: 'Core Features',
    icon: 'Zap',
    pages: [
      { 
        slug: 'chat-interface', 
        title: 'Chat Interface',
        description: 'Using the AI chat interface'
      },
      { 
        slug: 'ai-providers', 
        title: 'AI Providers',
        description: 'Configure OpenAI, Gemini, and local models'
      },
      { 
        slug: 'mcp-tools', 
        title: 'MCP Tools',
        description: 'Overview of the 128 available tools'
      },
      { 
        slug: 'browser-automation', 
        title: 'Browser Automation',
        description: 'Automate web tasks with Playwright'
      },
      { 
        slug: 'notes', 
        title: 'Notes',
        description: 'Create and organize notes with AI assistance'
      },
    ],
  },
  {
    id: 'advanced-features',
    title: 'Advanced Features',
    icon: 'Sparkles',
    pages: [
      {
        slug: 'rag-library',
        title: 'RAG Library',
        description: 'Document libraries with semantic search'
      },
      {
        slug: 'code-agent',
        title: 'Code Agent (Serena)',
        description: 'AI-powered IDE for software development'
      },
      {
        slug: 'dnd-assistant',
        title: 'D&D DM Assistant',
        description: 'D&D 5e tools for Dungeon Masters'
      },
      {
        slug: 'hooks-system',
        title: 'Hooks System',
        description: 'Customize AI behavior with hooks'
      },
      {
        slug: 'pr-review',
        title: 'PR Review Automation',
        description: 'Automate code reviews with AI'
      },
      {
        slug: 'automated-testing',
        title: 'Automated Web Testing',
        description: 'Create and run automated tests'
      },
      {
        slug: 'bug-reproduction',
        title: 'Bug Reproduction',
        description: 'Reproduce and document bugs automatically'
      },
      { 
        slug: 'scheduled-tasks', 
        title: 'Scheduled Tasks',
        description: 'Set up recurring automated tasks'
      },
      { 
        slug: 'dictation', 
        title: 'System Dictation',
        description: 'Global hotkey dictation with AI cleanup'
      },
      { 
        slug: 'windows-automation', 
        title: 'Windows Automation',
        description: 'Automate desktop applications'
      },
      { 
        slug: 'exploratory-testing', 
        title: 'Exploratory Testing',
        description: 'AI-assisted exploratory testing'
      },
      { 
        slug: 'dependency-map', 
        title: 'Dependency Map',
        description: 'Visualize project dependencies'
      },
      { 
        slug: 'recall', 
        title: 'Recall (Activity Tracking)',
        description: 'Track and search your activity history'
      },
    ],
  },
  {
    id: 'configuration',
    title: 'Configuration',
    icon: 'Settings',
    pages: [
      { 
        slug: 'settings-overview', 
        title: 'Settings Overview',
        description: 'Understanding Cogsmith settings'
      },
      { 
        slug: 'ai-configuration', 
        title: 'AI Configuration',
        description: 'Configure AI providers and models'
      },
      { 
        slug: 'mcp-servers', 
        title: 'MCP Servers',
        description: 'Enable and configure MCP servers'
      },
      { 
        slug: 'browser-profiles', 
        title: 'Browser Profiles',
        description: 'Manage browser profiles and settings'
      },
      { 
        slug: 'themes', 
        title: 'Themes',
        description: 'Customize the appearance'
      },
    ],
  },
  {
    id: 'guides',
    title: 'Guides',
    icon: 'Map',
    pages: [
      { 
        slug: 'azure-devops-setup', 
        title: 'Azure DevOps Setup',
        description: 'Connect to Azure DevOps'
      },
      { 
        slug: 'sql-database-setup', 
        title: 'SQL Database Setup',
        description: 'Connect to databases'
      },
      {
        slug: 'outlook-integration',
        title: 'Outlook Integration',
        description: 'Email and calendar integration'
      },
      {
        slug: 'dynamics-365-power-platform',
        title: 'Dynamics 365 & Power Platform',
        description: 'Power Automate flows and Dynamics 365 solutions'
      },
      {
        slug: 'workflow-examples',
        title: 'Workflow Examples',
        description: 'Common automation workflows'
      },
      {
        slug: 'best-practices',
        title: 'Best Practices',
        description: 'Tips for effective use'
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'AlertCircle',
    pages: [
      { 
        slug: 'common-issues', 
        title: 'Common Issues',
        description: 'Solutions to common problems'
      },
      { 
        slug: 'performance', 
        title: 'Performance',
        description: 'Optimize performance'
      },
      { 
        slug: 'faq', 
        title: 'FAQ',
        description: 'Frequently asked questions'
      },
    ],
  },
  {
    id: 'reference',
    title: 'Reference',
    icon: 'FileText',
    pages: [
      { 
        slug: 'keyboard-shortcuts', 
        title: 'Keyboard Shortcuts',
        description: 'Complete list of shortcuts'
      },
      { 
        slug: 'slash-commands', 
        title: 'Slash Commands',
        description: 'Available slash commands'
      },
      { 
        slug: 'tool-catalog', 
        title: 'Tool Catalog',
        description: 'Complete tool reference'
      },
      { 
        slug: 'changelog', 
        title: 'Changelog',
        description: 'Version history and updates'
      },
    ],
  },
];

// Helper function to get all pages
export const getAllPages = () => {
  return docsStructure.flatMap(section => 
    section.pages.map(page => ({
      ...page,
      section: section.id,
      sectionTitle: section.title,
      path: `/docs/${section.id}/${page.slug}`,
    }))
  );
};

// Helper function to find a page
export const findPage = (sectionId, slug) => {
  const section = docsStructure.find(s => s.id === sectionId);
  if (!section) return null;
  
  const page = section.pages.find(p => p.slug === slug);
  if (!page) return null;
  
  return {
    ...page,
    section: section.id,
    sectionTitle: section.title,
    path: `/docs/${sectionId}/${slug}`,
  };
};

// Helper function to get next/previous pages
export const getAdjacentPages = (sectionId, slug) => {
  const allPages = getAllPages();
  const currentIndex = allPages.findIndex(
    p => p.section === sectionId && p.slug === slug
  );
  
  return {
    previous: currentIndex > 0 ? allPages[currentIndex - 1] : null,
    next: currentIndex < allPages.length - 1 ? allPages[currentIndex + 1] : null,
  };
};

